// Typed client for the Automated Merchant/Tenant Provisioning Portal
// (/api/dpp/provisioning/*). The funnel: GI good → candidates → national
// business registry → map + mark → contact → tenant → EBSI onboarding.
import { api } from "./api";

export interface GiGood {
  id: string;
  name: string;
  type: string;
  country: string;
  region: string;
  authority: string;
  producerCount: number;
}

export type ProspectStage =
  | "candidate"
  | "contacted"
  | "interested"
  | "tenant_created"
  | "ebsi_onboarding"
  | "active"
  | "declined";

export interface Prospect {
  id: string;
  giProductId: string;
  giProductName: string;
  legalName: string;
  siren: string;
  siret: string;
  registryStatus: string;
  registeredAddress: string;
  lat: number;
  lng: number;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  companyRef: string;
  stage: ProspectStage;
  tenantOrgId: string;
  ebsiOnboardingId: string;
  ebsiDid?: string;
  ebsiRequestedAt?: string;
  ebsiRequestedBy?: string;
  notes: string;
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProspectInput {
  giProductId: string;
  legalName: string;
  siren?: string;
  siret?: string;
  registryStatus?: string;
  registeredAddress?: string;
  lat?: number;
  lng?: number;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  companyRef?: string;
  notes?: string;
  source?: string;
}

export const provisioning = {
  capabilities: () =>
    api.get<{ ebsiConfigured: boolean }>("/provisioning/capabilities"),
  giGoods: () =>
    api.get<{ goods: GiGood[] }>("/provisioning/gi-goods").then((r) => r.goods ?? []),
  prospects: () =>
    api
      .get<{ prospects: Prospect[] }>("/provisioning/prospects")
      .then((r) => r.prospects ?? []),
  createProspect: (input: CreateProspectInput) =>
    api.post<Prospect>("/provisioning/prospects", input),
  advanceStage: (id: string, stage: ProspectStage, note?: string) =>
    api.put<Prospect>(`/provisioning/prospects/${id}/stage`, { stage, note }),
  provisionTenant: (id: string) =>
    api.post<Prospect>(`/provisioning/prospects/${id}/tenant`),
  startEbsiOnboarding: (id: string) =>
    api.post<Prospect>(`/provisioning/prospects/${id}/ebsi-onboard`),
  approveEbsi: (id: string) =>
    api.post<Prospect>(`/provisioning/prospects/${id}/ebsi-approve`),
  ebsiStatus: (id: string) =>
    api.get<EbsiOnboardingStatus>(`/provisioning/prospects/${id}/ebsi-status`),
  prospect: (id: string) =>
    provisioning.prospects().then((ps) => ps.find((p) => p.id === id) ?? null),
  // The onboarding record started from a registry company (null if none yet).
  prospectByCompany: (companyRef: string) =>
    api
      .get<Prospect>(`/provisioning/company-prospect/${encodeURIComponent(companyRef)}`)
      .catch(() => null),
};

// Live onboarding state proxied from the EBSI platform (issuer/onboarding/:id).
export interface EbsiOnboardingStatus {
  id: string;
  tenantId?: string;
  entityType?: "TAO" | "TI";
  legalName?: string;
  siren?: string;
  siret?: string;
  applicantDid?: string;
  state:
    | "application_received"
    | "kyb_in_progress"
    | "kyb_completed"
    | "kyb_failed"
    | "pending_review"
    | "approved"
    | "did_created"
    | "accreditation_issued"
    | "active"
    | "rejected"
    | "revoked";
  kybResult?: Record<string, unknown>;
  reviewerNotes?: string;
}
